import { useEffect, useRef, useState } from "react";
import { useTranslation } from "@i18n";
import { usePortalChat, type FailedSend } from "../components/context";
import { ATTACHMENTS_MAX_COUNT, MESSAGE_TEXT_MAX_LENGTH, TYPING_EMIT_INTERVAL_MS } from "../portalChat.constants";

/**
 * The minimal shape a platform file must expose. Web passes DOM `File`s (name/type/size are own
 * properties); mobile passes expo picker descriptors ({ uri, name, type, size }).
 */
export type ComposerFile = { name: string; type?: string; size?: number };

export type PendingAttachment<TFile extends ComposerFile> = {
  id: string;
  file: TFile;
  key?: string;
  status: "uploading" | "done" | "error";
};

export type { FailedSend };

/**
 * The composer state machine, shared by the web thread pane and the mobile thread screen:
 * draft text (clamped to the zod cap), the pending-attachment upload machine (PRD §4.4 — a
 * message posts only once ALL attachments succeed; failed uploads are retried or removed first),
 * @-mention tracking (only mentions whose name survives in the text are submitted), the typing
 * throttle, and the failed-send bubble bound to ITS conversation.
 *
 * The uploader is injected because transports genuinely differ: web posts DOM File/Blob parts,
 * mobile posts RN `{uri,name,type}` descriptors — both to /fileSystemServer/uploadFileInTemp,
 * both resolving to a temp key (or null on failure).
 */
export function useChatComposer<TFile extends ComposerFile>({
  conversationId,
  memberCount,
  upload,
  onSendStarted,
}: {
  /** The OPEN conversation — the composer resets whenever it changes. */
  conversationId: string | null;
  /** Mention picker only offers itself in threads with more than one member. */
  memberCount: number;
  upload: (file: TFile) => Promise<string | null>;
  /** Fired as a send begins (web re-arms stick-to-bottom before the append lands). */
  onSendStarted?: () => void;
}) {
  const { t } = useTranslation();
  const chat = usePortalChat();

  const [text, setText] = useState("");
  const [pending, setPending] = useState<PendingAttachment<TFile>[]>([]);
  const [mentions, setMentions] = useState<{ _id: string; fullName: string }[]>([]);
  const [showMentionPicker, setShowMentionPicker] = useState(false);
  const [sending, setSending] = useState(false);
  // The failed-send bubble lives in the CORE so it survives a mobile screen unmount and keeps
  // its binding to ITS conversation; this hook only reads/writes it.
  const { failedSend, setFailedSend } = chat;

  const typingSentAtRef = useRef(0);
  const uploadRef = useRef(upload);
  uploadRef.current = upload;

  const lastConversationRef = useRef<string | null>(conversationId);
  useEffect(() => {
    // Reset only when a DIFFERENT conversation appears. A transient deselect (mobile camera flow
    // pushes the ImageEditor screen, blurring and deselecting the thread) keeps the draft and the
    // already-uploaded attachments intact for the return trip.
    if (!conversationId || lastConversationRef.current === conversationId) return;
    lastConversationRef.current = conversationId;
    setText("");
    setPending([]);
    setMentions([]);
    setShowMentionPicker(false);
  }, [conversationId]);

  const runUpload = async (item: PendingAttachment<TFile>) => {
    try {
      const key = await uploadRef.current(item.file);
      setPending((prev) =>
        prev.map((p) => (p.id === item.id ? { ...p, key: key ?? undefined, status: key ? "done" : "error" } : p)),
      );
    } catch {
      setPending((prev) => prev.map((p) => (p.id === item.id ? { ...p, status: "error" } : p)));
    }
  };

  const addFiles = async (files: TFile[]) => {
    if (!files.length) return;
    // Mirrors the zod cap: a message carries at most ATTACHMENTS_MAX_COUNT attachments.
    const room = ATTACHMENTS_MAX_COUNT - pending.length;
    if (room <= 0) {
      chat.showToast(t("A message can carry at most {{count}} attachments.", { count: ATTACHMENTS_MAX_COUNT }), false);
      return;
    }
    if (files.length > room) {
      chat.showToast(t("A message can carry at most {{count}} attachments.", { count: ATTACHMENTS_MAX_COUNT }), false);
    }
    const items: PendingAttachment<TFile>[] = files.slice(0, room).map((file) => ({
      id: `${file.name}-${Date.now()}-${Math.random()}`,
      file,
      status: "uploading" as const,
    }));
    setPending((prev) => [...prev, ...items]);
    await Promise.all(items.map(runUpload));
  };

  const retryUpload = async (item: PendingAttachment<TFile>) => {
    setPending((prev) => prev.map((p) => (p.id === item.id ? { ...p, status: "uploading" } : p)));
    await runUpload(item);
  };

  const removePending = (id: string) => setPending((prev) => prev.filter((p) => p.id !== id));

  const uploading = pending.some((p) => p.status === "uploading");
  const failed = pending.some((p) => p.status === "error");

  const resetComposer = () => {
    setText("");
    setPending([]);
    setMentions([]);
    chat.setComposerIntent(null);
  };

  const handleSend = async () => {
    if (!conversationId) return;
    const trimmed = text.trim();
    // PRD §4.4: the message posts only once ALL attachments succeed — a failed upload must be
    // retried or removed first.
    if (sending || uploading || failed) return;
    const readyAttachments = pending.flatMap((p) =>
      p.status === "done" && p.key
        ? [{ key: p.key, name: p.file.name, type: p.file.type || undefined, size: p.file.size }]
        : [],
    );
    if (!trimmed && !readyAttachments.length) return;
    setSending(true);
    onSendStarted?.();
    // Only mentions whose inserted @Name is still present in the text count (PRD: mentions are
    // what the sender actually left in the message).
    const liveMentions = mentions.filter((m) => trimmed.includes(m.fullName)).map((m) => m._id);
    const attempt: FailedSend = {
      conversationId,
      text: trimmed,
      attachments: readyAttachments,
      replyToMessageId: chat.composerIntent?.kind === "reply" ? chat.composerIntent.message._id : undefined,
      mentions: liveMentions,
    };
    const ok = await chat.sendMessage(attempt);
    setSending(false);
    if (ok) {
      // The slot is core-global: only clear a bubble that belongs to THIS conversation — a
      // success in chat B must not silently discard chat A's unsent text.
      if (failedSend?.conversationId === conversationId) setFailedSend(null);
      resetComposer();
    } else {
      // PRD §4.16 "Message failed to send (online)": an inline failed bubble with Retry; the
      // composer is freed for the next message.
      setFailedSend(attempt);
      resetComposer();
    }
  };

  const retryFailedSend = async () => {
    if (!failedSend || sending) return;
    setSending(true);
    const ok = await chat.sendMessage(failedSend);
    setSending(false);
    if (ok) setFailedSend(null);
  };

  const handleTextChange = (value: string) => {
    setText(value.slice(0, MESSAGE_TEXT_MAX_LENGTH));
    if (value.endsWith("@") && memberCount > 1) setShowMentionPicker(true);
    else setShowMentionPicker(false);
    if (!conversationId) return;
    const nowMs = Date.now();
    if (nowMs - typingSentAtRef.current > TYPING_EMIT_INTERVAL_MS) {
      typingSentAtRef.current = nowMs;
      chat.emitTyping(conversationId, true);
    }
  };

  /** Appends "Full Name " to the draft and registers the mention (deduped). */
  const insertMention = (member: { _id: string; fullName: string }) => {
    setShowMentionPicker(false);
    setText((prev) => `${prev}${member.fullName} `);
    setMentions((prev) => (prev.some((m) => m._id === member._id) ? prev : [...prev, member]));
  };

  /** Append a fragment (emoji picks) respecting the length cap. All-or-nothing: slicing a
   *  multi-unit emoji at the cap would strand a lone surrogate and store mojibake. */
  const insertText = (fragment: string) => {
    setText((prev) => (prev.length + fragment.length <= MESSAGE_TEXT_MAX_LENGTH ? `${prev}${fragment}` : prev));
  };

  const dismissFailedSend = () => setFailedSend(null);

  return {
    text,
    pending,
    mentions,
    showMentionPicker,
    setShowMentionPicker,
    sending,
    uploading,
    failed,
    failedSend,
    addFiles,
    retryUpload,
    removePending,
    handleSend,
    retryFailedSend,
    handleTextChange,
    insertMention,
    insertText,
    dismissFailedSend,
  };
}
